"use client";

import { memo } from "react";
import { useTranslations } from "next-intl";

import { ProStyledDisplayName } from "@/shared/ui/data-display/pro-styled-display-name";
import { SafeAvatar } from "@/shared/ui/data-display/safe-avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";
import type { FriendListItem } from "@/shared/api/friends";
import { useTournamentFriends } from "@/hooks/useFriends";

const MAX_AVATARS = 3;

interface FriendsInTournamentBadgeProps {
  tournamentId: string;
  friends?: FriendListItem[];
  count?: number;
  isAuthenticated: boolean;
  className?: string;
}

function FriendsInTournamentBadgeInner({
  tournamentId,
  friends: friendsProp,
  count: countProp,
  isAuthenticated,
  className,
}: FriendsInTournamentBadgeProps) {
  const t = useTranslations("Tournaments.card");
  const { data, isLoading } = useTournamentFriends(
    tournamentId,
    isAuthenticated && friendsProp === undefined,
  );
  const friends = friendsProp ?? data?.friends ?? [];
  const count = countProp ?? data?.count ?? 0;

  if (!isAuthenticated || count === 0) return null;
  if (isLoading && friends.length === 0) return null;

  const displayFriends = friends.slice(0, MAX_AVATARS);
  const extra = count > MAX_AVATARS ? count - MAX_AVATARS : 0;

  const trigger = (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-foreground hover:bg-primary/20 transition-colors",
        className,
      )}
    >
      <span className="flex -space-x-2">
        {displayFriends.map((f) => (
          <SafeAvatar
            key={f.id}
            src={f.avatarUrl ?? undefined}
            alt={f.displayName || f.username || ""}
            size="sm"
            allowAnimatedGif={Boolean(f.hasPro)}
            className="h-6 w-6 border-2 border-background rounded-full"
          />
        ))}
      </span>
      {extra > 0 && <span className="text-muted-foreground">+{extra}</span>}
      <span className="text-muted-foreground">
        {count === 1 ? t("friendsInTournamentOne") : t("friendsInTournament")}
      </span>
    </button>
  );

  if (friends.length === 0) return trigger;

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="space-y-1">
          {friends.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm"
            >
              <SafeAvatar
                src={f.avatarUrl ?? undefined}
                alt={f.displayName || f.username || ""}
                size="md"
                allowAnimatedGif={Boolean(f.hasPro)}
                className="h-8 w-8 shrink-0 rounded-full"
              />
              <ProStyledDisplayName
                displayName={f.displayName || f.username || f.id}
                hasPro={f.hasPro}
                proNicknameStyle={f.proNicknameStyle}
                proNicknameColorA={f.proNicknameColorA}
                proNicknameColorB={f.proNicknameColorB}
                className="truncate font-medium min-w-0"
              />
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const FriendsInTournamentBadge = memo(FriendsInTournamentBadgeInner);
